import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/Layout/DashboardLayout';
import TextInput from '../../components/FormElements/TextInput';
import PasswordInput from '../../components/FormElements/PasswordInput';
import SelectInput from '../../components/FormElements/SelectInput';
import AlertMessage from '../../components/ui/AlertMessage';
import { useAuth } from '../../contexts/AuthContext';
import FormSpinnerOverlay from '../../components/ui/FormSpinnerOverlay';
import defaultUserProfileImage from '../../assets/img/user-profile.jpg';
import PencilFillIcon from '../../components/icons/PencilFillIcon';
import { useLatinAmericaCountryOptions } from '../../hooks/useCountryOptions';
import 'react-phone-input-2/lib/style.css';
import PhoneInput from 'react-phone-input-2';

const ProfileSupplierScreen = () => {
    const { user, token, updateUserInContext } = useAuth();
    const countryOptions = useLatinAmericaCountryOptions();

    // Personal data
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        birthDate: '',
        country: '',
        newPassword: '',
        repeatPassword: '',
        profileImageFile: null,
        profileImagePreview: defaultUserProfileImage,
    });

    // Company data
    const [companyData, setCompanyData] = useState({
        companyName: '',
        companyEmail: '',
        companyPhoneNumber: '',
        companyCountry: '',
        companyAddress: '',
        companyCity: '',
    });

    const [message, setMessage] = useState({ type: '', text: '' });
    const [companyMessage, setCompanyMessage] = useState({ type: '', text: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isCompanySubmitting, setIsCompanySubmitting] = useState(false);

    useEffect(() => {
        if (user) {
            let determinedProfileImage = defaultUserProfileImage;
            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

            if (user.avatar_file_id) {
                determinedProfileImage = `${API_BASE_URL}/files/serve/${user.avatar_file_id}`;
            } else if (user.image && typeof user.image === 'string' && user.image.startsWith('http')) {
                determinedProfileImage = user.image;
            }

            setFormData(prev => ({
                ...prev,
                name: user.name || user.fullName || user.username || '',
                email: user.email || '',
                birthDate: user.birth_date ? user.birth_date.split('T')[0] : '',
                country: user.country || '',
                profileImagePreview: determinedProfileImage,
                newPassword: '',
                repeatPassword: '',
                profileImageFile: null,
            }));

            setCompanyData(prev => ({
                ...prev,
                companyName: user.companyName || '',
                companyEmail: user.companyEmail || '',
                companyPhoneNumber: user.phoneNumber || '',
                companyCountry: user.country || '',
                companyAddress: user.companyAddress || '',
                companyCity: user.city || '',
            }));
        } else {
            setFormData({
                name: '',
                email: '',
                birthDate: '',
                country: '',
                newPassword: '',
                repeatPassword: '',
                profileImageFile: null,
                profileImagePreview: defaultUserProfileImage,
            });
            setCompanyData({
                companyName: '',
                companyEmail: '',
                companyPhoneNumber: '',
                companyCountry: '',
                companyAddress: '',
                companyCity: '',
            });
        }
    }, [user]);

    useEffect(() => {
        const currentPreview = formData.profileImagePreview;
        if (currentPreview && currentPreview.startsWith('blob:')) {
            return () => {
                URL.revokeObjectURL(currentPreview);
            };
        }
    }, [formData.profileImagePreview]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleCompanyChange = (e) => {
        const { name, value } = e.target;
        setCompanyData(prev => ({ ...prev, [name]: value }));
    };

    const handleImageChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setFormData(prev => ({
                ...prev,
                profileImageFile: file,
                profileImagePreview: URL.createObjectURL(file)
            }));
        }
    };

    // Personal data submit
    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage({ type: '', text: '' });
        setIsSubmitting(true);

        if (formData.newPassword && formData.newPassword !== formData.repeatPassword) {
            setMessage({ type: 'danger', text: 'Las nuevas contraseñas no coinciden.' });
            setIsSubmitting(false);
            return;
        }

        if (!user || !user.id) {
            setMessage({ type: 'danger', text: 'Error: No se pudo identificar al proveedor.' });
            setIsSubmitting(false);
            return;
        }

        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

        try {
            let newUploadedAvatarFileId = null;

            if (formData.profileImageFile) {
                const imageFormData = new FormData();
                imageFormData.append('avatar', formData.profileImageFile);

                const imageUploadResponse = await fetch(`${API_BASE_URL}/api/files/upload/avatar`, {
                    method: 'POST',
                    body: imageFormData,
                    headers: { ...(token && { 'Authorization': `Bearer ${token}` }) },
                });

                if (!imageUploadResponse.ok) {
                    const errorData = await imageUploadResponse.json().catch(() => ({}));
                    throw new Error(errorData.message || `Error al subir la imagen: ${imageUploadResponse.statusText}`);
                }
                const uploadResult = await imageUploadResponse.json();
                newUploadedAvatarFileId = uploadResult.file_id;
            }

            const supplierUpdatePayload = {
                email: user.email,
                username: formData.name,
                initial_form_json: user.initial_form_json || null,
                birth_date: formData.birthDate,
                country: formData.country,
            };
            if (formData.newPassword) {
                supplierUpdatePayload.password = formData.newPassword;
            }
            if (newUploadedAvatarFileId) {
                supplierUpdatePayload.avatar_file_id = newUploadedAvatarFileId;
            } else if (user.avatar_file_id && !formData.profileImageFile) {
                supplierUpdatePayload.avatar_file_id = user.avatar_file_id;
            } else {
                supplierUpdatePayload.avatar_file_id = null;
            }

            const supplierUpdateResponse = await fetch(`${API_BASE_URL}/api/suppliers/${user.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token && { 'Authorization': `Bearer ${token}` }),
                },
                body: JSON.stringify(supplierUpdatePayload),
            });

            if (!supplierUpdateResponse.ok) {
                const errorData = await supplierUpdateResponse.json().catch(() => ({}));
                throw new Error(errorData.message || `Error al actualizar el perfil: ${supplierUpdateResponse.statusText}`);
            }

            const updatedSupplierFromServer = await supplierUpdateResponse.json();

            const finalSupplierDataForContext = {
                ...user,
                ...updatedSupplierFromServer,
                username: supplierUpdatePayload.username || user.username,
                initial_form_json: supplierUpdatePayload.initial_form_json !== undefined ? supplierUpdatePayload.initial_form_json : user.initial_form_json,
                birth_date: supplierUpdatePayload.birth_date || user.birth_date,
                country: supplierUpdatePayload.country || user.country,
                avatar_file_id: supplierUpdatePayload.avatar_file_id,
            };

            updateUserInContext(finalSupplierDataForContext);

            let determinedProfileImage = defaultUserProfileImage;
            if (finalSupplierDataForContext.avatar_file_id) {
                determinedProfileImage = `${API_BASE_URL}/api/files/serve/${finalSupplierDataForContext.avatar_file_id}`;
            }

            setFormData(prev => ({
                ...prev,
                name: finalSupplierDataForContext.username || '',
                birthDate: finalSupplierDataForContext.birth_date ? finalSupplierDataForContext.birth_date.split('T')[0] : '',
                country: finalSupplierDataForContext.country || '',
                profileImagePreview: determinedProfileImage,
                profileImageFile: null,
                newPassword: '',
                repeatPassword: '',
            }));

            setMessage({ type: 'success', text: 'Perfil de proveedor actualizado con éxito.' });

        } catch (error) {
            setMessage({ type: 'danger', text: error.message || 'Error al guardar los cambios.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Company data submit
    const handleCompanySubmit = async (e) => {
        e.preventDefault();
        setCompanyMessage({ type: '', text: '' });
        setIsCompanySubmitting(true);

        if (!user || !user.id) {
            setCompanyMessage({ type: 'danger', text: 'Error: No se pudo identificar al proveedor.' });
            setIsCompanySubmitting(false);
            return;
        }

        if (!companyData.companyName || !companyData.companyEmail || !companyData.companyPhoneNumber || !companyData.companyCity) {
            setCompanyMessage({ type: 'danger', text: 'Por favor, completa todos los campos obligatorios.' });
            setIsCompanySubmitting(false);
            return;
        }

        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

        try {
            const companyUpdatePayload = {
                companyName: companyData.companyName,
                companyEmail: companyData.companyEmail,
                phoneNumber: companyData.companyPhoneNumber,
                city: companyData.companyCity,
                country: companyData.companyCountry,
                companyAddress: companyData.companyAddress,
            };

            const companyUpdateResponse = await fetch(`${API_BASE_URL}/api/suppliers/${user.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token && { 'Authorization': `Bearer ${token}` }),
                },
                body: JSON.stringify(companyUpdatePayload),
            });

            if (!companyUpdateResponse.ok) {
                const errorData = await companyUpdateResponse.json().catch(() => ({}));
                throw new Error(errorData.message || `Error al actualizar el perfil de empresa: ${companyUpdateResponse.statusText}`);
            }

            setCompanyMessage({ type: 'success', text: 'Perfil de empresa actualizado con éxito.' });

            // Fetch updated supplier data and update context/state
            const updatedSupplierResponse = await fetch(`${API_BASE_URL}/api/suppliers/${user.id}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token && { 'Authorization': `Bearer ${token}` }),
                },
            });
            if (updatedSupplierResponse.ok) {
                const updatedSupplier = await updatedSupplierResponse.json();
                updateUserInContext(updatedSupplier);

                setCompanyData(prev => ({
                    ...prev,
                    companyName: updatedSupplier.companyName || '',
                    companyEmail: updatedSupplier.companyEmail || '',
                    companyPhoneNumber: updatedSupplier.phoneNumber || '',
                    companyCountry: updatedSupplier.country || '',
                    companyAddress: updatedSupplier.companyAddress || '',
                    companyCity: updatedSupplier.city || '',
                }));
            }

        } catch (error) {
            setCompanyMessage({ type: 'danger', text: error.message || 'Error al guardar los cambios de empresa.' });
        } finally {
            setIsCompanySubmitting(false);
        }
    };

    return (
        <DashboardLayout activeMenuItem="Perfil Proveedor" isScrollable={true}>
            <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-2 mb-3">
                <h1 className="h2 mb-4">Perfil de Proveedor</h1>
            </div>
            {/* Personal Data Section */}
            <div className="row text-start mb-4">
                <div className="col-12">
                    <div className="card card-metric border-0">
                        <FormSpinnerOverlay isLoading={isSubmitting} />
                        <div className="card-body">
                            {message.text && <AlertMessage type={message.type} message={message.text} />}
                            <form id="profileForm" onSubmit={handleSubmit}>
                                <div className="row">
                                    <div className="col-md-3 text-center mb-3 mb-md-0">
                                        <div className="position-relative mx-auto" style={{ width: '150px', height: '150px' }}>
                                            <img
                                                src={formData.profileImagePreview || defaultUserProfileImage}
                                                alt="Avatar"
                                                className="img-fluid rounded-circle shadow-sm"
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            />
                                            <label htmlFor="profileImageInput" className="btn btn-sm btn-checkin position-absolute p-2 rounded-circle" style={{ bottom: '5px', right: '5px', cursor: 'pointer', lineHeight: 1, boxShadow: '0 0 5px rgba(0,0,0,0.2)' }} title="Editar imagen">
                                                <PencilFillIcon />
                                                <span className="visually-hidden">Editar imagen</span>
                                            </label>
                                            <input type="file" id="profileImageInput" name="profileImage" className="d-none" accept="image/*" onChange={handleImageChange} />
                                        </div>
                                    </div>
                                    <div className="col-md-5 mb-3 mb-md-0 text-start">
                                        <TextInput
                                            label="Nombre completo"
                                            id="profileName"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            required
                                        />
                                        <TextInput
                                            label="Email"
                                            id="profileEmail"
                                            name="email"
                                            type="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            required
                                            disabled
                                        />
                                        <TextInput
                                            label="Fecha de nacimiento"
                                            id="birthDate"
                                            name="birthDate"
                                            type="date"
                                            value={formData.birthDate}
                                            onChange={handleChange}
                                            iconClass="bi bi-calendar3"
                                            ariaLabel="Fecha de nacimiento"
                                        />
                                    </div>
                                    <div className="col-md-4 text-start">
                                        <div className="mb-3">
                                            <SelectInput
                                                label="País"
                                                id="country"
                                                name="country"
                                                value={formData.country}
                                                onChange={handleChange}
                                                options={countryOptions}
                                                defaultOptionText="Selecciona un país..."
                                            />
                                        </div>
                                        <div className="mb-3">
                                            <PasswordInput
                                                label="Nueva contraseña"
                                                id="newPassword"
                                                name="newPassword"
                                                value={formData.newPassword}
                                                onChange={handleChange}
                                                autoComplete="new-password"
                                            />
                                        </div>
                                        <div className="mb-3">
                                            <PasswordInput
                                                label="Repita la nueva contraseña"
                                                id="repeatPassword"
                                                name="repeatPassword"
                                                value={formData.repeatPassword}
                                                onChange={handleChange}
                                                autoComplete="new-password"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="row mt-4">
                                    <div className="col-12 text-end">
                                        <button type="submit" className="btn btn-checkin px-4">Guardar Cambios</button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
            {/* Company Data Section */}
            <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-2 mb-3">
                <h3 className="h3">Perfil Empresa</h3>
            </div>
            <div className="row text-start mb-4">
                <div className="col-12">
                    <div className="card card-metric border-0">
                        <FormSpinnerOverlay isLoading={isCompanySubmitting} />
                        <div className="card-body">
                            {companyMessage.text && <AlertMessage type={companyMessage.type} message={companyMessage.text} />}
                            <form id="companyForm" onSubmit={handleCompanySubmit}>
                                <div className="row">
                                    <div className="col-md-3 text-center mb-3 mb-md-0">
                                        {/* Reuse profile image for company, or allow separate upload if needed */}
                                        <div className="position-relative mx-auto" style={{ width: '150px', height: '150px' }}>
                                            <img
                                                src={formData.profileImagePreview || defaultUserProfileImage}
                                                alt="Avatar"
                                                className="img-fluid rounded-circle shadow-sm"
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            />
                                            <label htmlFor="companyProfileImageInput" className="btn btn-sm btn-checkin position-absolute p-2 rounded-circle" style={{ bottom: '5px', right: '5px', cursor: 'pointer', lineHeight: 1, boxShadow: '0 0 5px rgba(0,0,0,0.2)' }} title="Editar imagen">
                                                <PencilFillIcon />
                                                <span className="visually-hidden">Editar imagen</span>
                                            </label>
                                            <input type="file" id="companyProfileImageInput" name="companyProfileImage" className="d-none" accept="image/*" disabled />
                                        </div>
                                    </div>
                                    <div className="col-md-5 mb-3 mb-md-0 text-start">
                                        <TextInput
                                            label="Nombre del emprendimiento o empresa"
                                            id="companyProfileName"
                                            name="companyName"
                                            value={companyData.companyName}
                                            onChange={handleCompanyChange}
                                            required
                                        />
                                        <TextInput
                                            label="Email de la empresa"
                                            id="companyProfileEmail"
                                            name="companyEmail"
                                            type="email"
                                            value={companyData.companyEmail}
                                            onChange={handleCompanyChange}
                                            required
                                        />
                                        <div className="mb-3">
                                            <label htmlFor="companyPhoneNumber" className="form-label">Número de WhatsApp</label>
                                            <PhoneInput
                                                country={'cl'}
                                                value={companyData.companyPhoneNumber}
                                                onChange={phone => setCompanyData(prev => ({ ...prev, companyPhoneNumber: phone }))}
                                                inputProps={{
                                                    name: 'companyPhoneNumber',
                                                    id: 'companyPhoneNumber',
                                                    required: true,
                                                    className: 'form-control'
                                                }}
                                                inputStyle={{ width: '100%' }}
                                                specialLabel=""
                                                enableSearch
                                                disableDropdown={false}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-4 text-start">
                                        <TextInput
                                            label="Dirección"
                                            id="companyLocation"
                                            name="companyAddress"
                                            value={companyData.companyAddress}
                                            onChange={handleCompanyChange}
                                            required
                                        />
                                        <TextInput
                                            label="Ciudad"
                                            id="companyCity"
                                            name="companyCity"
                                            value={companyData.companyCity}
                                            onChange={handleCompanyChange}
                                            required
                                        />
                                        <div className="mb-3">
                                            <SelectInput
                                                label="País"
                                                id="companyCountry"
                                                name="companyCountry"
                                                value={companyData.companyCountry}
                                                onChange={handleCompanyChange}
                                                options={countryOptions}
                                                defaultOptionText="Selecciona un país..."
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="row mt-4">
                                    <div className="col-12 text-end">
                                        <button type="submit" className="btn btn-checkin px-4">Guardar Cambios</button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );
};

export default ProfileSupplierScreen;